/* mifoa — gallery swipe
 * ---------------------------------------------------------------------------
 * Every PDP gallery could only be advanced by tapping a thumbnail. This adds
 * swipe, without rewriting a single gallery.
 *
 * The catalogue ships four different gallery implementations, one per design
 * port, but all four share the same shape: thumbnails and slides paired by a
 * numeric index on a data attribute.
 *
 *   data-gallery-thumb   / data-gallery-main    12 PDPs, incl. Pandit Ji, Kutir
 *   data-mf-object-thumb / data-mf-object-slide  FLIQ, JetDock
 *   data-woolyv2-thumb   / data-woolyv2-stage    Wooly
 *   data-parke-thumb     / data-parke-slide      Parke
 *
 * So rather than reimplement four galleries, a swipe resolves to a target index
 * and clicks that gallery's own thumbnail. Whatever each one already does on a
 * thumbnail click — cross-fade, active border, counter, lightbox wiring — keeps
 * happening, unchanged and untouched. If a gallery's behaviour is edited later,
 * swipe inherits the edit for free.
 *
 * Deliberate choices:
 *
 * · Every input, not just touch. Finger and stylus drag, mouse drag, and a
 *   two-finger trackpad flick all move the gallery. The first cut was touch-only
 *   on the theory that a mouse drag ending on the stage is indistinguishable from
 *   a click, which would open Mr Bones' lightbox by accident — but the click
 *   suppression below already solves that, and touch-only silently does nothing
 *   when the page is being checked in a narrow desktop window, which is exactly
 *   how it gets checked.
 *
 * · touch-action:pan-y on the stage, rather than preventDefault on touchmove.
 *   It tells the browser up front that vertical scrolling is still its job and
 *   horizontal is ours, so the page never stutters and no listener has to be
 *   non-passive.
 *
 * · A real swipe swallows the click that follows it, so swiping across an image
 *   never opens a lightbox by accident. A tap still opens it.
 *
 * · Wraps at both ends. On a phone, hitting an invisible wall at the last image
 *   reads as broken rather than as a boundary.
 *
 * Additive and self-contained: no gallery markup, CSS or section file is
 * modified. Removing this file restores the previous behaviour exactly.
 */
(() => {
  'use strict';

  if (window.__mfGallerySwipe) return;
  window.__mfGallerySwipe = true;

  /* `count` is only set on the first scheme, and only because that scheme's handler
     never updated its counter — it has read "1 / 10" whatever image you were on since
     the day it was written, by thumbnail as much as by swipe. FLIQ, JetDock and Parke
     drive their own counters correctly, so they are left alone; writing to a counter
     that already works would only create a second author for the same number. */
  var PATTERNS = [
    { thumb: 'data-gallery-thumb',   slide: 'data-gallery-main',   count: '[data-gallery-count]' },
    { thumb: 'data-mf-object-thumb', slide: 'data-mf-object-slide' },
    { thumb: 'data-woolyv2-thumb',   slide: 'data-woolyv2-stage' },
    { thumb: 'data-parke-thumb',     slide: 'data-parke-slide'   }
  ];

  // Enough travel to be a decision rather than a wobble, and capped so it stays
  // reachable with a thumb on a narrow phone.
  var MIN_TRAVEL = 36;
  var travelFor = function (width) {
    return Math.max(MIN_TRAVEL, Math.min(70, width * 0.12));
  };

  var num = function (el, attr) {
    var v = parseInt(el.getAttribute(attr), 10);
    return isNaN(v) ? null : v;
  };

  /* A page carries a desktop layout and a mobile layout, each with its own copy
     of the gallery, so scope has to be resolved per stage rather than per page.
     Walk up from the slides until an ancestor also holds thumbnails: that is the
     one gallery, whichever layout it belongs to. */
  var scopeFor = function (stage, thumbAttr) {
    var node = stage;
    for (var i = 0; i < 6 && node; i++) {
      if (node.querySelector('[' + thumbAttr + ']')) return node;
      node = node.parentElement;
    }
    return null;
  };

  var wire = function (stage, pattern) {
    if (stage.dataset.mfSwipe === '1') return false;

    var scope = scopeFor(stage, pattern.thumb);
    if (!scope) return false;

    var thumbs = [].slice.call(scope.querySelectorAll('[' + pattern.thumb + ']'))
      .filter(function (t) { return num(t, pattern.thumb) !== null; })
      .sort(function (a, b) { return num(a, pattern.thumb) - num(b, pattern.thumb); });

    if (thumbs.length < 2) return false;

    stage.dataset.mfSwipe = '1';
    stage.style.touchAction = 'pan-y';

    /* Index is tracked rather than read back, because each of the four galleries
       marks its active thumbnail differently — an inline border colour here, a
       class there. Listening in the capture phase means taps on thumbnails keep
       this in step, including the synthetic ones fired below. */
    var index = 0;
    var initial = thumbs.filter(function (t) {
      return /(^|\s)(is-active|is-selected|active|selected)(\s|$)/.test(t.className || '') ||
             t.getAttribute('aria-selected') === 'true' ||
             t.getAttribute('aria-current') === 'true' ||
             t.getAttribute('aria-pressed') === 'true'; // Wooly's thumbnails are <button>s
    })[0];
    if (initial) index = thumbs.indexOf(initial);

    /* Rewrite only the leading number, reading the separator and the total back out of
       whatever is already on the page. "1 / 10" stays "2 / 10"; a product that formats
       it "1 of 10" or "1—10" keeps its own punctuation. Nothing to keep in sync with the
       markup, and a format this never anticipated simply leaves the counter alone. */
    var counter = pattern.count ? scope.querySelector(pattern.count) : null;
    var setCount = function () {
      if (!counter) return;
      var m = /^(\s*)(\d+)(\D+?)(\d+)(\s*)$/.exec(counter.textContent || '');
      if (!m) return;
      counter.textContent = m[1] + (index + 1) + m[3] + m[4] + m[5];
    };

    scope.addEventListener('click', function (event) {
      var hit = event.target.closest ? event.target.closest('[' + pattern.thumb + ']') : null;
      if (!hit) return;
      var i = thumbs.indexOf(hit);
      if (i !== -1) { index = i; setCount(); }
    }, true);

    var goTo = function (i) {
      var next = ((i % thumbs.length) + thumbs.length) % thumbs.length;
      if (next === index) return;
      index = next;
      thumbs[next].click();
      setCount();
    };

    setCount();

    var startX = 0, startY = 0, axis = '', active = false, swiped = false;

    stage.addEventListener('pointerdown', function (event) {
      if (!event.isPrimary) return;
      if (event.pointerType === 'mouse') {
        if (event.button !== 0) return;
        // Without this the browser starts dragging the image itself, ghost and all.
        event.preventDefault();
      }
      active = true; swiped = false; axis = '';
      startX = event.clientX; startY = event.clientY;
    }, { passive: false });

    stage.addEventListener('pointermove', function (event) {
      if (!active) return;
      var dx = event.clientX - startX;
      var dy = event.clientY - startY;
      // Lock to whichever axis the gesture commits to first, then stop guessing.
      if (!axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') active = false;
      }
    }, { passive: true });

    // Belt and braces alongside the preventDefault above, for images that set draggable.
    stage.addEventListener('dragstart', function (event) {
      if (active) event.preventDefault();
    });

    var finish = function (event) {
      if (!active) { active = false; return; }
      active = false;
      if (axis !== 'x') return;
      var dx = event.clientX - startX;
      if (Math.abs(dx) < travelFor(stage.getBoundingClientRect().width || 320)) return;
      swiped = true;
      goTo(index + (dx < 0 ? 1 : -1));
    };

    stage.addEventListener('pointerup', finish, { passive: true });
    stage.addEventListener('pointercancel', function () { active = false; }, { passive: true });

    /* A drag that ends on the image would otherwise land as a click, and on some
       PDPs a click opens the lightbox. Swallow exactly one. A tap or a plain click
       is unaffected, because neither ever sets this flag. */
    stage.addEventListener('click', function (event) {
      if (!swiped) return;
      swiped = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    /* Trackpads don't drag, they flick: two fingers sideways arrives as a wheel
       event carrying deltaX. Worth handling on its own because it is how a laptop
       user instinctively moves a gallery, and because preventDefault here also stops
       macOS reading the same flick as swipe-to-go-back and leaving the page. */
    var wheelX = 0, wheelIdle = null, wheelSpent = false;
    stage.style.overscrollBehaviorX = 'contain';
    stage.addEventListener('wheel', function (event) {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      clearTimeout(wheelIdle);
      wheelIdle = setTimeout(function () { wheelX = 0; wheelSpent = false; }, 260);
      if (wheelSpent) return;
      wheelX += event.deltaX;
      if (Math.abs(wheelX) > 80) {
        goTo(index + (wheelX > 0 ? 1 : -1));
        wheelX = 0;
        wheelSpent = true; // one image per flick, however long the tail runs on
      }
    }, { passive: false });

    return true;
  };

  var init = function () {
    var wired = 0;

    PATTERNS.forEach(function (pattern) {
      var slides = [].slice.call(document.querySelectorAll('[' + pattern.slide + ']'));
      if (!slides.length) return;

      /* Always wire the slides' parent, never a slide itself. Wooly is why: it keeps
         its images in <template> tags and hydrates them on demand, so at load only one
         slide exists and the rest appear as you move through them. Binding to a slide
         would bind to whichever one happened to exist first — which Wooly then hides.
         The parent is the one element that is present from the start and stays put, and
         it is the stage the finger actually lands on in every one of the four galleries. */
      var stages = [];
      slides.forEach(function (slide) {
        var parent = slide.parentElement;
        if (parent && stages.indexOf(parent) === -1) stages.push(parent);
      });

      stages.forEach(function (stage) {
        if (wire(stage, pattern)) wired++;
      });
    });

    return wired;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  // Sections can render late in the theme editor and on slow connections.
  window.addEventListener('load', init);
  setTimeout(init, 1200);

  window.__mfGallerySwipeInit = init;
})();
